import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CheckoutState,
  updateCheckoutState,
  BillingAddress,
} from "../checkout";
import {
  failShippingLocationValidation,
  failAsyncCashIneligibleStateValidation,
  returnedInvalidAddress,
  suggestAddress,
  userAcceptedSuggestedAddress,
  userAcceptedMissingApartmentAddress,
  failNoPOBoxValidation,
  formAddressUpdatedWithSuggestedAddress,
  setValidationErrorReasons,
} from "./action-creators";
import { ValidateAddressResponseProto_ValidationErrorReasonProto_Reason } from "@noom/noom-contracts/noom_contracts/billing/address/address_validation_service";

export interface CheckoutPhysicalAddressState {
  address: BillingAddress;
  isSameAsBillingAddress: boolean;
  // TODO (cory): Modal state does not belong in Redux. But the checkout flow is very complicated...
  showSuggestedAddressModal: boolean;
  showInvalidAddressModal: boolean;
  showShippingLocationInvalidModal: boolean;
  showAsyncCashIneligibleStateModal: boolean;
  // The corrected address returned by the validation API
  suggestedAddress: Partial<BillingAddress>;
  chosenModalAddress: Partial<BillingAddress>;
  // Whether or not to show an error message due to failed TR validation
  addressInvalid: boolean;
  // Whether or not to show an error message due to failed max length validation
  isAddressLineInvalid: boolean;
  // Whether the user accepted one of the choices in the Suggested Address modal. ie, they did not
  // go back to editing
  acceptedSuggestedAddress: boolean;
  // Whether the user accepted the address with missing apartment number from the missing apartment modal
  acceptedMissingApartmentAddress: boolean;
  shouldUpdateFormAddress: boolean;
  showNoPoBoxModal: boolean;
  // Whether the address has been validated using a backend endpoint
  addressIsValidated: boolean;
  // Validation error reasons from the address validation API
  validationErrorReasons: ValidateAddressResponseProto_ValidationErrorReasonProto_Reason[];
}

const initialState: CheckoutPhysicalAddressState = {
  address: {} as BillingAddress,
  isSameAsBillingAddress: false,
  showSuggestedAddressModal: false,
  showInvalidAddressModal: false,
  showShippingLocationInvalidModal: false,
  showAsyncCashIneligibleStateModal: false,
  suggestedAddress: {},
  chosenModalAddress: {},
  addressInvalid: false,
  isAddressLineInvalid: false,
  acceptedSuggestedAddress: false,
  acceptedMissingApartmentAddress: false,
  shouldUpdateFormAddress: false,
  showNoPoBoxModal: false,
  addressIsValidated: false,
  validationErrorReasons: [],
};

const checkoutPhysicalAddressSlice = createSlice({
  name: "checkoutPhysicalAddress",
  initialState,
  reducers: {
    setAddress: (state, action: PayloadAction<BillingAddress>) => ({
      ...state,
      address: action.payload,
      // When the user changes the address, clear the error message
      addressInvalid: false,
      isAddressLineInvalid: false,
    }),
    setAddressValidated: (state, action: PayloadAction<boolean>) => ({
      ...state,
      addressIsValidated: action.payload,
    }),
    toggleSameAsBillingAddress: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isSameAsBillingAddress: action.payload,
    }),
    closeModal: (state) => ({
      ...state,
      showSuggestedAddressModal: false,
      showInvalidAddressModal: false,
      showNoPoBoxModal: false,
      showShippingLocationInvalidModal: false,
      showAsyncCashIneligibleStateModal: false,
      chosenModalAddress: {},
      validationErrorReasons: [],
    }),
    failAddressLineValidation: (state) => ({
      ...state,
      isAddressLineInvalid: true,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(failShippingLocationValidation, (state) => ({
        ...state,
        showShippingLocationInvalidModal: true,
      }))
      .addCase(failAsyncCashIneligibleStateValidation, (state) => ({
        ...state,
        showAsyncCashIneligibleStateModal: true,
      }))
      .addCase(userAcceptedSuggestedAddress, (state, action) => {
        const ret = { ...state };
        if (!action.payload.isSameAsBillingAddress) {
          ret.address = {
            ...state.address,
            ...action.payload.suggestedAddress,
          };
        }
        ret.chosenModalAddress = action.payload.suggestedAddress;
        ret.showSuggestedAddressModal = false;
        ret.addressInvalid = false;
        ret.acceptedSuggestedAddress = true;
        ret.shouldUpdateFormAddress = action.payload.shouldUpdateFormAddress;
        ret.addressIsValidated = true;
        return ret;
      })
      .addCase(
        suggestAddress,
        (state, action: PayloadAction<Partial<BillingAddress>>) => ({
          ...state,
          showSuggestedAddressModal: true,
          suggestedAddress: action.payload,
          addressInvalid: true,
          acceptedSuggestedAddress: false,
        })
      )
      .addCase(returnedInvalidAddress, (state) => ({
        ...state,
        showInvalidAddressModal: true,
        suggestedAddress: {},
        chosenModalAddress: {},
        addressInvalid: true,
      }))
      .addCase(
        updateCheckoutState,
        // If their physical address is the same as billing address, clear errors when they update
        // billing address
        (state, action: PayloadAction<Partial<CheckoutState>>) => {
          if (!state.isSameAsBillingAddress) {
            return state;
          }
          if (!action.payload.billingAddress) {
            return state;
          }
          return {
            ...state,
            addressInvalid: false,
            isAddressLineInvalid: false,
          };
        }
      )
      .addCase(failNoPOBoxValidation, (state) => ({
        ...state,
        showNoPoBoxModal: true,
      }))
      .addCase(formAddressUpdatedWithSuggestedAddress, (state) => ({
        ...state,
        shouldUpdateFormAddress: false,
      }))
      .addCase(
        setValidationErrorReasons,
        (
          state,
          action: PayloadAction<
            ValidateAddressResponseProto_ValidationErrorReasonProto_Reason[]
          >
        ) => ({
          ...state,
          validationErrorReasons: action.payload,
          acceptedMissingApartmentAddress: false,
          addressInvalid: true,
        })
      )
      .addCase(userAcceptedMissingApartmentAddress, (state) => ({
        ...state,
        acceptedMissingApartmentAddress: true,
        showInvalidAddressModal: false,
        showSuggestedAddressModal: false,
        showNoPoBoxModal: false,
        validationErrorReasons: [],
        addressInvalid: false,
        addressIsValidated: true,
      }));
  },
});

export const {
  setAddress,
  toggleSameAsBillingAddress,
  closeModal,
  failAddressLineValidation,
  setAddressValidated,
} = checkoutPhysicalAddressSlice.actions;

export default checkoutPhysicalAddressSlice;
